import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios, { Method } from "axios";
import { useEffect, useRef } from "react";
import { AuthHelper } from "../utils/api/AuthHelper";

interface UseApiResourceOptions<TUpdate> {
  queryKey: string[];
  url: string;
  method?: Method;
  enabled?: boolean;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  fetchOnPost?: boolean;
  fetchPayload?: any; // Payload to be sent with the POST method
  keepPreviousDataOnFetch?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useApiResource<TData = any, TUpdate = any>({
  queryKey,
  url,
  method = "PUT",
  enabled = true,
  refetchInterval,
  refetchOnWindowFocus = false,
  fetchOnPost = false,
  fetchPayload = null,
  keepPreviousDataOnFetch = false,
  onSuccess,
  onError,
}: UseApiResourceOptions<TUpdate>) {
  const queryClient = useQueryClient();

  const msalInstance = useMsal().instance as PublicClientApplication;

  const isFirstLoad = useRef(true);
  const serializedQueryKey = JSON.stringify(queryKey);
  const previousQueryKey = useRef(serializedQueryKey);

  if (previousQueryKey.current !== serializedQueryKey) {
    previousQueryKey.current = serializedQueryKey;
    isFirstLoad.current = true;
  }

  const query = useQuery<TData>({
    queryKey,
    queryFn: async ({ signal }) => {
      const token = await AuthHelper.getAccessToken(msalInstance);
      const headers = {
        "x-api-key": process.env.REACT_APP_APIM_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json;charset=UTF-8",
      };

      const axiosConfig = {
        url,
        method: "GET",
        signal,
        headers,
        ...(fetchOnPost === true && { method: "POST", data: fetchPayload }),
      };

      const res = await axios.request(axiosConfig);
      return res?.data ?? {};
    },
    enabled,
    refetchOnWindowFocus,
    refetchInterval,
    placeholderData: keepPreviousDataOnFetch ? keepPreviousData : undefined,
  });

  // Track first load using useEffect
  useEffect(() => {
    if (query.isSuccess && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [query.isSuccess]);

  const mutation = useMutation({
    mutationFn: async (payload: TUpdate) => {
      const token = await AuthHelper.getAccessToken(msalInstance);
      const res = await axios.request({
        url: url,
        method,
        data: payload,
        headers: {
          "x-api-key": process.env.REACT_APP_APIM_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json;charset=UTF-8",
        },
      });
      return res?.data ?? {};
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });

  const showLoader = query.isLoading && isFirstLoad.current;

  return {
    ...query,
    update: mutation.mutate,
    updateAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isUpdateSuccess: mutation.isSuccess,
    isUpdateError: mutation.isError,
    updateError: mutation.error,
    showLoader,
  };
}
